import type { AccountPaymentMethod } from '@cocoonspace/sdk-js/types/accountPaymentMethod'
import {
	getGetAccountPaymentMethodsQueryKey,
	useDeleteAccountPaymentMethod,
	useGetAccountPaymentMethods,
	useSetDefaultAccountPaymentMethod,
} from '@cocoonspace/sdk-js/domains/account/account'
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from '@cocoonspace/ui/components/alert-dialog'
import { Button } from '@cocoonspace/ui/components/button'
import { Card } from '@cocoonspace/ui/components/card'
import { useLingui } from '@lingui/react/macro'
import { useQueryClient } from '@tanstack/react-query'
import { LandmarkIcon, Trash2Icon } from 'lucide-react'
import { usePathname } from 'next/navigation'
import { toast } from 'sonner'

import { PaymentMethodCardIcon } from '~/domains/payments/components/payment-method-card-icon'
import { stripLocaleFromPath } from '~/i18n/routing'
import { useTracking } from '~/providers/tracking-provider'

export const PaymentMethodsList = ({
	onSetDefault,
}: {
	onSetDefault?: VoidFunction
}) => {
	const pathname = stripLocaleFromPath(usePathname() ?? '')
	const queryClient = useQueryClient()
	const { t } = useLingui()
	const { track } = useTracking()

	const { data: paymentMethods } = useGetAccountPaymentMethods({
		query: {
			select: (res) => res.data.filter((pm) => pm.type === 'card') || [],
		},
	})

	const pmDefaultMutation = useSetDefaultAccountPaymentMethod()
	const deletePmMutation = useDeleteAccountPaymentMethod()

	const setDefault = (pm: AccountPaymentMethod) =>
		pmDefaultMutation.mutateAsync({ methodId: pm.id! }).then(() => {
			onSetDefault?.()

			toast.success(t`Votre carte par défaut a été mise à jour`)

			queryClient.invalidateQueries({
				queryKey: getGetAccountPaymentMethodsQueryKey(),
			})
			track.paymentMethodUpdate({ action: 'set-default', route: pathname })
		})

	const onDelete = (pm: AccountPaymentMethod) =>
		deletePmMutation.mutateAsync({ methodId: pm.id! }).then(() => {
			toast.success(t`Votre carte a été supprimée`)

			queryClient.invalidateQueries({
				queryKey: getGetAccountPaymentMethodsQueryKey(),
			})

			track.paymentMethodUpdate({ action: 'delete', route: pathname })
		})

	if (!paymentMethods?.length) return null

	return (
		<div className='flex flex-1 flex-col gap-4'>
			{paymentMethods?.map((paymentMethod) => (
				<Card
					className='flex flex-col divide-y'
					key={paymentMethod.id}
				>
					<div className='flex w-full flex-1 flex-row items-center gap-4 p-4'>
						<div className='px-2 text-[40px]'>
							{paymentMethod.type === 'sepa_credit' && (
								<LandmarkIcon
									size={30}
									strokeWidth={1}
								/>
							)}

							{paymentMethod.type === 'card' && (
								<PaymentMethodCardIcon brand={paymentMethod.card?.brand} />
							)}
						</div>

						<div className='relative flex flex-1 flex-col'>
							{paymentMethod.default && (
								<div className='absolute -top-4 -right-4 rounded-tr rounded-bl bg-slate-800 px-6 py-1 text-sm text-white'>
									{t`Carte par défaut`}
								</div>
							)}

							<div>{paymentMethod.title}</div>

							<strong className='text-lg'>{paymentMethod.name}</strong>

							<div className='flex w-full flex-col md:flex-row md:gap-2'>
								<div>{`**** **** **** ${paymentMethod.card?.last_4}`}</div>

								<div className='md:ml-auto'>
									{t`Exp le : ${paymentMethod.card?.exp_month?.toString().padStart(2, '0')}/${paymentMethod.card?.exp_year?.toString()}`}
								</div>
							</div>
						</div>
					</div>

					{paymentMethods?.length > 1 && (
						<div className='flex flex-row items-center justify-center gap-4 p-4 md:justify-end'>
							{!paymentMethod.default && (
								<Button
									onClick={() => setDefault(paymentMethod)}
									size='sm'
									variant='outline'
								>
									{t`Choisir par défaut`}
								</Button>
							)}

							<AlertDialog>
								<AlertDialogTrigger asChild>
									<Button
										className='gap-1'
										size='sm'
										variant='outline'
									>
										<Trash2Icon size={16} />
										{t`Supprimer`}
									</Button>
								</AlertDialogTrigger>

								<AlertDialogContent>
									<AlertDialogHeader>
										<AlertDialogTitle>{t`Êtes-vous sûr.e de vouloir supprimer cette carte bancaire ?`}</AlertDialogTitle>
									</AlertDialogHeader>

									<AlertDialogFooter>
										<AlertDialogCancel>{t`Annuler`}</AlertDialogCancel>

										<AlertDialogAction onClick={() => onDelete(paymentMethod)}>
											{t`Supprimer`}
										</AlertDialogAction>
									</AlertDialogFooter>
								</AlertDialogContent>
							</AlertDialog>
						</div>
					)}
				</Card>
			))}
		</div>
	)
}
